import React from "react";
import ReactDOMServer from "react-dom/server";
import htmlToPdfmake from "html-to-pdfmake";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { type ContentTable, Text } from "@bmi-digital/react-pdf-maker";
import type { SVGImport } from "@bmi-digital/svg-import";
import type { RoofV2 as Roof, RoofDimensionsData } from "../../types/roof";
import type { GetMicroCopy } from "../../../MicroCopy";

type Props = {
  roof: Roof;
  dimensions: RoofDimensionsData;
  getMicroCopy: GetMicroCopy;
};

const SelectedRoofPDF = ({ roof, dimensions, getMicroCopy }: Props) => (
  <>
    <RoofInfoSection
      title={getMicroCopy(microCopy.RESULTS_ROOF_SECTION_TITLE)}
      roofName={getMicroCopy(microCopy.ROOF_SELECTION_ROOF, {
        name: roof.name
      })}
      lengthValues={dimensions.roofLengthValues}
      angleValues={dimensions.roofAngleValues}
      illustration={roof.illustration}
      getMicroCopy={getMicroCopy}
    />
    {dimensions.protrusion ? (
      <RoofInfoSection
        title={getMicroCopy(microCopy.RESULTS_PROTRUSION_SECTION_TITLE)}
        roofName={getMicroCopy(microCopy.RESULTS_SELECTED_PROTRUSION_NAME, {
          name: dimensions.protrusion.name
        })}
        lengthValues={dimensions.protrusionLengthValues}
        angleValues={dimensions.protrusionAngleValues}
        illustration={dimensions.protrusion.illustration}
        getMicroCopy={getMicroCopy}
      />
    ) : undefined}
  </>
);

const RoofInfoSection = ({
  roofName,
  lengthValues,
  angleValues,
  getMicroCopy,
  illustration,
  title
}: {
  roofName: string;
  lengthValues: Record<string, string>;
  angleValues: Record<string, string>;
  getMicroCopy: GetMicroCopy;
  illustration: SVGImport;
  title: string;
}) => {
  const roofLengthsCount = Object.keys(lengthValues).length;

  const content: ContentTable = {
    margin: [0, 0, 0, 12],
    table: {
      widths: [100, "*"],
      body: [
        [
          {
            stack: [getRoofIllustration(illustration, roofName)],
            margin: [0, 12, 0, 12]
          },
          {
            stack: [
              getDimensionsTable({
                getMicroCopy,
                lengthValues,
                angleValues
              })
            ],
            margin: [6, roofLengthsCount <= 4 ? 38 : 22, 0, 0]
          }
        ]
      ]
    },
    layout: {
      //this can be properly tested after the implementation of visual tests
      hLineWidth: /* istanbul ignore next */ (i, node) => {
        return i === 0 || i === node.table.body.length ? 1 : 0;
      },
      vLineWidth: /* istanbul ignore next */ (i, node) => {
        return i === 0 || i === node.table.widths?.length ? 1 : 0;
      },
      hLineColor: /* istanbul ignore next */ () => "#cccccc",
      vLineColor: /* istanbul ignore next */ () => "#cccccc"
    }
  };

  return (
    <>
      <Text bold={true} margin={[0, 4, 0, 8]}>
        {title}
      </Text>
      {content}
    </>
  );
};

const getRoofIllustration = (
  Illustration: SVGImport,
  roofOrProtrusionName: string
) =>
  htmlToPdfmake(
    ReactDOMServer.renderToStaticMarkup(
      <div>
        <Illustration width="96px" height="77px" />
        <span
          style={{
            textAlign: "center",
            color: "#0072B0"
          }}
        >
          {roofOrProtrusionName}
        </span>
      </div>
    )
  );

const getDimensionsTable = ({
  getMicroCopy,
  lengthValues,
  angleValues
}: {
  getMicroCopy: GetMicroCopy;
  lengthValues: Record<string, string>;
  angleValues: Record<string, string>;
}) => {
  const roofLengthCells = Object.entries(lengthValues).map(([key, value]) => (
    <>
      <td>
        <span style={{ fontWeight: 700 }}>
          {getMicroCopy(microCopy.RESULTS_ROOF_SIDE_LABEL)} {key}:
        </span>
      </td>
      <td>
        <span style={{ fontWeight: 400 }}>{value}</span>
      </td>
    </>
  ));

  const roofAngleCells = Object.entries(angleValues).map(([key, value]) => (
    <>
      <td>
        <span style={{ fontWeight: 700 }}>
          {getMicroCopy(microCopy.RESULTS_ROOF_ANGLE_LABEL)} {key}:
        </span>
      </td>
      <td>
        <span style={{ fontWeight: 400 }}>{value}</span>
      </td>
    </>
  ));

  const roofLengthRows = splitTableCellsInChunks(roofLengthCells);
  const roofAngleRows = splitTableCellsInChunks(roofAngleCells);

  const dimensionsTableMarkup = ReactDOMServer.renderToStaticMarkup(
    <table style={{ backgroundColor: "transparent" }}>
      <tbody>
        {roofLengthRows.map((row, idx) => (
          <tr key={`roof-length-row-${idx}`}>{row}</tr>
        ))}

        {roofAngleRows.map((row, idx) => (
          <tr key={`roof-angle-row-${idx}`}>{row}</tr>
        ))}
      </tbody>
    </table>
  );

  const dimensionsTablePdf = htmlToPdfmake(dimensionsTableMarkup) as [
    ContentTable
  ];

  dimensionsTablePdf[0].layout = {
    //this can be properly tested after the implementation of visual tests
    hLineWidth: /* istanbul ignore next */ () => 0,
    vLineWidth: /* istanbul ignore next */ () => 0,
    paddingRight: /* istanbul ignore next */ (columnIndex) => {
      // padding for labels
      if ([0, 2, 4].includes(columnIndex)) {
        return 2;
      }

      // padding columns
      return 12;
    },
    paddingBottom: /* istanbul ignore next */ () => 10
  };

  return dimensionsTablePdf;
};

function splitTableCellsInChunks(
  cells: React.JSX.Element[]
): React.JSX.Element[][] {
  const cellsPerRow = 4;
  const result: React.JSX.Element[][] = [];

  for (let i = 0; i < cells.length; i += cellsPerRow) {
    const items = cells.slice(i, i + cellsPerRow);

    result.push(
      items.concat(
        new Array(cellsPerRow - items.length).fill(
          <>
            <td />
            <td />
          </>
        )
      )
    );
  }

  return result;
}

export default SelectedRoofPDF;
